import { useState } from 'react';
import { TICKET_NUMBER_PATTERN, type TicketLinkCreateRequest } from '@traceio-org/platform-contracts';
import { RadioGroup, TextField } from '../../components/fields';
import { WriteDialog } from '../../components/WriteDialog';
import { linearKeyOf } from './ticketFormat';

const KINDS = [
  {
    value: 'linear',
    label: 'Linear issue',
    description: 'Shown as a chip with its key. Trace only; nothing syncs.',
  },
  { value: 'related', label: 'Related ticket', description: 'Another ticket about the same problem.' },
  {
    value: 'duplicate_of',
    label: 'Duplicate of',
    description: 'Closes this ticket as Duplicate and tells the customer which ticket it was merged into.',
  },
] as const;

type Kind = (typeof KINDS)[number]['value'];

/**
 * Link a Linear issue or another ticket (POST …/links). A Linear link needs an https://linear.app/…/issue/KEY-123 URL:
 * the key is read from it and shown before saving, as the chip will show it.
 */
export function TicketLinkDialog({
  open,
  onClose,
  ticketNumber,
  onLink,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  ticketNumber: string;
  onLink: (request: TicketLinkCreateRequest) => Promise<unknown>;
  onSaved: (description: string) => void;
}) {
  const [kind, setKind] = useState<Kind>('linear');
  const [url, setUrl] = useState('');
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);
  const key = linearKeyOf(url);
  const saved = kind === 'linear' ? (key ?? '') : target.trim().toUpperCase();

  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title={`Link ${ticketNumber}`}
      submitLabel="Add link"
      onSubmit={async () => {
        if (kind === 'linear') {
          const found = !url.trim()
            ? 'Paste the Linear issue URL.'
            : !key
              ? 'Use a Linear issue URL: https://linear.app/<workspace>/issue/TE-812.'
              : null;
          setError(found);
          if (found) return 'invalid';
          await onLink({ kind: 'linear', url: url.trim(), key: key! });
          return undefined;
        }
        const number = target.trim().toUpperCase();
        const found = !number
          ? 'Enter the other ticket number.'
          : !TICKET_NUMBER_PATTERN.test(number)
            ? 'Use a ticket number such as NWF-142.'
            : number === ticketNumber
              ? 'A ticket cannot link to itself.'
              : null;
        setError(found);
        if (found) return 'invalid';
        await onLink({ kind, targetNumber: number });
        return undefined;
      }}
      onSaved={() =>
        onSaved(kind === 'duplicate_of' ? `Marked as a duplicate of ${saved}.` : `Linked ${saved}.`)
      }
    >
      <RadioGroup
        legend="Link"
        choices={KINDS}
        value={kind}
        onChange={(value) => {
          setKind(value as Kind);
          setError(null);
        }}
      />
      {kind === 'linear' ? (
        <TextField
          label="Linear issue URL"
          type="text"
          inputMode="text"
          value={url}
          onChange={setUrl}
          error={error}
          placeholder="https://linear.app/trace/issue/TE-812"
          hint={
            key ? (
              <>
                Shown as <span className="admin-link-chip">{key}</span>
              </>
            ) : (
              'The key is read from the URL.'
            )
          }
          spellCheck={false}
          required
        />
      ) : (
        <TextField
          label="Ticket number"
          value={target}
          onChange={setTarget}
          error={error}
          placeholder="NWF-118"
          spellCheck={false}
          required
        />
      )}
    </WriteDialog>
  );
}
